import { useState } from 'react';
import { useStorageSlot } from '@gonfalon/browser-storage/useStorageSlot';
import { enableLoginRememberEmail } from '@gonfalon/dogfood-flags/dogfood-flags';

import { useBeastMode, useFindLoginPage, useLoginV2 } from '#actions/auth';
import { setTimeToLoginEndTime as setTimeToLoginEndTimeAction } from '#actions/timeToLoginMetric';
import { withForm } from '#components/withForm';
import { useDispatch } from '#hooks/useDispatch';
import type { LoginInfo } from '#utils/loginUtils';
import { createLoginInfo, validateLoginInfo, validateLoginInfoWithEmailOnly } from '#utils/loginUtils';

import type { LoginFormProps } from './LoginForm';
import { LoginForm } from './LoginForm';

const useRedux = () => {
  const dispatch = useDispatch();
  const setTimeToLoginEndTime = (endTime: number) => dispatch(setTimeToLoginEndTimeAction(endTime));
  return {
    setTimeToLoginEndTime,
  };
};

type WrapperProps = {
  onBeastMode?: (info: LoginInfo, redirectPath: string) => void;
  onLoginV2?: (info: LoginInfo, redirectPath?: string) => Promise<void>;
  onGetLoginPage?: (info: LoginInfo, redirectPath?: string) => Promise<unknown>;
  setTimeToLoginEndTime?: (endTime: number) => void;
};

const REMEMBERED_EMAIL_SLOT_KEY = 'login-remembered-email';

const WrappedLoginForm = withForm<LoginInfo>({
  initialState(props) {
    return createLoginInfo({ username: props.beastModeEmail || props.rememberedEmail || '' });
  },
  validate(record, props) {
    return props.requirePassword ? validateLoginInfo(record) : validateLoginInfoWithEmailOnly(record);
  },
  onSubmit(props, modified) {
    let final = modified;
    if (props.beastMode) {
      final = final.set('username', '');
      props.onBeastMode(final, props.redirect);
      return;
    }
    if (props?.requirePassword) {
      props.setTimeToLoginEndTime(Date.now());
    }

    if (!props?.requirePassword) {
      props.onGetLoginPage(final, props.redirect);
    } else {
      props.onLoginV2(final, props.redirect);
    }
  },
})<LoginFormProps, WrapperProps>(LoginForm);

type LoginFormContainerProps = LoginFormProps & {
  redirect?: string;
  beastModeEmail?: string;
};

function useRememberedEmail() {
  const enableRememberEmail = enableLoginRememberEmail();
  const [rememberedEmail, rememberedEmailSlot] = useStorageSlot(REMEMBERED_EMAIL_SLOT_KEY);
  const [initialRememberedEmail] = useState(() => (enableRememberEmail ? rememberedEmail : null));
  const [rememberMe, setRememberMe] = useState(() => rememberedEmail !== null);

  const onRememberMeChange = (checked: boolean) => {
    setRememberMe(checked);
    if (!checked) {
      rememberedEmailSlot.remove();
    }
  };

  const persistRememberedEmail = (email: string) => {
    if (!enableRememberEmail) {
      return;
    }
    if (rememberMe) {
      rememberedEmailSlot.set(email);
    } else {
      rememberedEmailSlot.remove();
    }
  };

  return {
    rememberedEmail: initialRememberedEmail ?? undefined,
    rememberMe,
    onRememberMeChange,
    persistRememberedEmail,
  };
}

export function LoginFormContainer(props: LoginFormContainerProps) {
  const onBeastMode = useBeastMode();
  const handleGetLoginPage = useFindLoginPage();
  const onLoginV2 = useLoginV2();
  const { setTimeToLoginEndTime } = useRedux();
  const { rememberedEmail, rememberMe, onRememberMeChange, persistRememberedEmail } = useRememberedEmail();

  const onGetLoginPage = async (info: LoginInfo, redirectPath?: string) => {
    persistRememberedEmail(info.username);
    return handleGetLoginPage(info, redirectPath);
  };

  const handleLoginV2 = async (info: LoginInfo, redirectPath?: string) => {
    persistRememberedEmail(info.username);
    return onLoginV2(info, redirectPath);
  };

  return (
    <WrappedLoginForm
      {...props}
      setTimeToLoginEndTime={setTimeToLoginEndTime}
      onLoginV2={handleLoginV2}
      onBeastMode={onBeastMode}
      onGetLoginPage={onGetLoginPage}
      rememberedEmail={rememberedEmail}
      rememberMe={rememberMe}
      onRememberMeChange={onRememberMeChange}
    />
  );
}
